import { AITooltipText } from '@features/messaging/constants';
import { useGetAIChatSuggestion } from '@features/messaging/hooks/useGetAIChatSuggestion';
import { Close } from '@mui/icons-material';
import AssistantIcon from '@mui/icons-material/Assistant';
import { LoadingButton } from '@mui/lab';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  IconButton,
  Divider,
  Alert,
} from '@mui/material';
import { getStrippedText } from '@utils/helper';
import parse from 'html-react-parser';
import React, { useState } from 'react';

interface Props {
  isOpen: boolean;
  handleInsertAIContent: (content: string) => void;
  handleClose: () => void;
}

const AIPromptModal = ({
  isOpen,
  handleInsertAIContent,
  handleClose,
}: Props) => {
  const [prompt, setPrompt] = useState('');
  const [response, setResponse] = useState('');
  const [errorText, setErrorText] = useState('');
  const { sendAIPrompt, isSendingPromptLoading } = useGetAIChatSuggestion({
    setErrorText,
  });

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    const cleanedPrompt = prompt.replace(/[\n\r]+/g, ' ');

    const options = {
      onSuccess: (data: {
        status: number;
        message: string;
        content: string;
      }) => {
        if (data && data?.status === 200) {
          setErrorText('');
          setResponse(data?.content);
        } else {
          setResponse(data?.message);
        }
      },
    };

    const payload = {
      prompt: cleanedPrompt,
    };

    sendAIPrompt(
      {
        ...payload,
      },
      options,
    );
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
        <br />
        <AssistantIcon
          sx={{
            fontSize: 60,
            color: 'primary.main',
            margin: 'auto !important',
          }}
        />
        <Typography variant="h3" sx={{ pt: 3 }}>
          Write with AI
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {errorText && <Alert severity="error">{errorText}</Alert>}
        {!response && (
          <TextField
            label="Insert your prompt here"
            placeholder={AITooltipText}
            fullWidth
            focused
            multiline
            minRows={2}
            margin="normal"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
        )}

        {(() => {
          if (response) {
            return (
              <Box mt={4}>
                <Typography>
                  ❔ Your Prompt: <b>{prompt}</b>
                </Typography>

                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  🧠 AI Response
                </Typography>

                <Divider />
                {parse(response.replace(/\n\s*/g, ''))}
              </Box>
            );
          }

          return null;
        })()}
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: 'center',
          marginBottom: '20px',
          marginTop: '10px',
        }}
      >
        <Button
          sx={{ width: '200px', fontSize: '1rem', padding: '5px' }}
          onClick={handleClose}
          color="secondary"
          variant="outlined"
          disabled={isSendingPromptLoading}
        >
          Cancel
        </Button>
        {!response ? (
          <LoadingButton
            onClick={handleGenerate}
            color="primary"
            variant="contained"
            disabled={
              isSendingPromptLoading ||
              !prompt ||
              getStrippedText(prompt).length === 0
            }
            loading={isSendingPromptLoading}
            sx={{
              width: '200px',
              fontSize: '1rem',
              fontWeight: 'bold',
              padding: '5px',
            }}
          >
            Generate
          </LoadingButton>
        ) : (
          <>
            <Button
              onClick={() => {
                setResponse('');
              }}
              variant="outlined"
              color="primary"
              sx={{ width: '200px', fontSize: '1rem', padding: '5px' }}
            >
              Try Again
            </Button>
            <Button
              onClick={() => handleInsertAIContent(response)}
              variant="contained"
              color="success"
              sx={{ width: '200px', fontSize: '1rem', padding: '5px' }}
              disabled={!response.trim()}
            >
              Insert
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AIPromptModal;
